import { capitalCase } from 'change-case';
// @mui
import { Card, Container, Tab, Tabs } from '@mui/material';
import { styled } from '@mui/material/styles';
// routes
// hooks
import useSettings from '../../hooks/useSettings';
import useTabs from '../../hooks/useTabs';
// components
import { useMemo } from 'react';
import { Outlet, useNavigate, useParams } from 'react-router';
import { useLocation } from 'react-router-dom';
import LoadingScreen from 'src/components/LoadingScreen';
import { Club, useGetClubQuery } from 'src/graphql/generated';
import useAuth from 'src/hooks/useAuth';
import Page from '../../components/Page';
import ClubCover from './ClubCover';
import { navItems } from './navItems';

// ----------------------------------------------------------------------

const TabsWrapperStyle = styled('div')(({ theme }) => ({
  zIndex: 9,
  bottom: 0,
  width: '100%',
  display: 'flex',
  position: 'absolute',
  backgroundColor: theme.palette.background.paper,
  [theme.breakpoints.up('sm')]: {
    justifyContent: 'center',
  },
  [theme.breakpoints.up('md')]: {
    justifyContent: 'flex-end',
    paddingRight: theme.spacing(3),
  },
}));

export type ClubLayoutContext = {
  club: Club;
};

export default function ClubLayout() {
  const { themeStretch } = useSettings();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const ACCOUNT_TABS = useMemo(() => navItems(user?.role), [user?.role]);

  const { id } = useParams();

  const { data, loading } = useGetClubQuery({
    variables: {
      id: id || '',
    },
  });

  const defaultTab = () => {
    const matchingTab = ACCOUNT_TABS.find((tab) => location.pathname.includes(tab.path));
    return matchingTab?.value || 'detail';
  };

  const { currentTab, onChangeTab } = useTabs(defaultTab());

  if (loading) {
    return <LoadingScreen />;
  }

  const club: Club | undefined = data?.getClub as Club;

  return (
    <Page title={`Club: ${club?.name}`}>
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <Card
          sx={{
            mb: 3,
            height: { xs: 288, md: 180 },
            position: 'relative',
          }}
        >
          <ClubCover club={club} />

          <TabsWrapperStyle>
            <Tabs
              allowScrollButtonsMobile
              variant="scrollable"
              scrollButtons="auto"
              value={currentTab}
              onChange={onChangeTab}
            >
              {ACCOUNT_TABS.map((tab) => (
                <Tab
                  disableRipple
                  key={tab.value}
                  label={capitalCase(tab.value)}
                  icon={tab.icon}
                  value={tab.value}
                  onClick={() => navigate(tab.path)}
                />
              ))}
            </Tabs>
          </TabsWrapperStyle>
        </Card>

        <Outlet context={{ club }} />
      </Container>
    </Page>
  );
}
